import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services';

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const idToken = sessionStorage.getItem('access_token');
  const _router = inject(Router);
  const _authService = inject(AuthService);

  if (idToken) {
    // console.log('[PD-DEBUG] HTTP →', req.method, req.url);
    const cloned = req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + idToken),
    });

    return next(cloned).pipe(
      catchError((error: HttpErrorResponse) => {
        // console.error('[PD-DEBUG] HTTP ✗', error.status, req.method, req.url, error.error);

        // A 401 caused by a role/authorization mismatch cannot be fixed by
        // refreshing — the refreshed token carries the same role, so a refresh
        // here just yields another 401. Detect that case from the API message
        // and sign out immediately rather than entering the refresh path.
        const message = typeof error.error?.message === 'string' ? error.error.message.toLowerCase() : '';
        const isRoleMismatch = error.status === 401 && message.includes('does not match the authorized role');

        if (error.status === 401 && !isRoleMismatch) {
          // console.warn('[PD-DEBUG] 401 → attempting token refresh for', req.url);
          return _authService.refreshToken().pipe(
            switchMap((newToken: string) => {
              const updatedRequest = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${newToken}`),
              });
              // Retry the original request exactly once. If it still fails, the
              // error propagates to the catchError below (sign out) — we never
              // re-enter the refresh cycle.
              return next(updatedRequest);
            }),
            catchError((refreshError: unknown) => {
              // console.error('[PD-DEBUG] refresh FAILED after 401 → logging out. Triggered by', req.url, refreshError);
              _authService.signOut();
              _router.navigate(['/login'], { queryParams: { error: 'Session expired. Please log in again.' } });
              return throwError(() => refreshError);
            }),
          );
        } else if (error.status === 403 || isRoleMismatch) {
          // console.error('[PD-DEBUG] authorization failure → logging out (no refresh).', error.status, req.url);
          _authService.signOut();
          _router.navigate(['/login'], {
            queryParams: { error: error.status === 403 ? 'Unauthorized access.' : 'Your role is not authorized.' },
          });
        }
        return throwError(() => error);
      }),
    );
  } else {
    return next(req);
  }
};
